<template>
  <v-container id="process_instance_details_root" fluid>
    <!-- TOP BREADCRUMB -->
    <v-row class="mb-4 px-4" align="center">
      <!-- BACK BUTTON -->
      <v-col cols="auto">
        <v-btn text small @click="$router.go(-1)">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('trustx.processInstanceDetails.back') }}
        </v-btn>
      </v-col>
      <v-col>
        <span class="text-caption grey--text">
          {{ $t('trustx.processInstanceDetails.breadcrumb.processInstances') }} &gt;
        </span>
        <span class="text-caption primary--text font-weight-medium">
          {{ instance.referenceId }}
        </span>
      </v-col>

      <v-col v-if="documentExists" cols="auto">
        <v-btn color="primary" :loading="downloadDocumentLoading" @click="downloadDocument">
          <v-icon left>mdi-archive-arrow-down-outline</v-icon>
          {{ $t('trustx.downloadPdfTitle') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- OVERVIEW -->
    <v-card class="mb-4 pa-4">
      <v-card-title class="justify-space-between">
        <span class="font-weight-bold">{{ $t('trustx.processInstanceDetails.overview.title') }}</span>
        <v-chip small :color="getStatusColor(instance.status)" dark>
          {{ getLocalizedStatus(instance.status) }}
        </v-chip>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <v-row class="mx-0">
          <!-- Face image -->
          <v-col cols="12" md="3" class="text-center">
            <v-avatar size="140">
              <div v-if="faceImageLoading" class="image-loading image-loading--avatar">
                <v-progress-circular indeterminate color="primary" />
              </div>
              <v-img v-else-if="faceImage" :src="faceImage" />
              <div v-else class="image-loading image-loading--avatar">
                <v-icon color="grey">mdi-image-off</v-icon>
              </div>
            </v-avatar>
          </v-col>

          <!-- Basic data -->
          <v-col cols="12" md="9">
            <v-row>
              <v-col cols="12" md="6">
                <div class="detail-row">
                  <span class="label">{{ $t('trustx.processInstanceDetails.overview.name') }}:</span>
                  <span>{{ instance.name || $t('trustx.notAvailable') }}</span>
                </div>

                <div class="detail-row">
                  <span class="label">{{ $t('trustx.processInstanceDetails.overview.email') }}:</span>
                  <span>{{ instance.email || $t('trustx.notAvailable') }}</span>
                </div>

                <div class="detail-row">
                  <span class="label">{{ $t('trustx.processInstanceDetails.overview.phone') }}:</span>
                  <span>{{ instance.phone || $t('trustx.notAvailable') }}</span>
                </div>

                <div class="detail-row">
                  <span class="label">{{ $t('trustx.processInstanceDetails.overview.applicationDate') }}:</span>
                  <span>{{ instance.applicationDate }}</span>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="detail-row">
                  <span class="label">{{ $t('trustx.processInstanceDetails.overview.processVersion') }}:</span>
                  <span>{{ instance.processVersion }}</span>
                </div>

                <div class="detail-row">
                  <span class="label">{{ $t('trustx.processInstanceDetails.overview.referenceId') }}:</span>
                  <span>{{ instance.referenceId }}</span>
                </div>

                <div class="detail-row">
                  <span class="label">{{ $t('trustx.processInstanceDetails.overview.totalTime') }}:</span>
                  <span>{{ instance.totalTime }}</span>
                </div>

                <div class="detail-row">
                  <span class="label">{{ $t('trustx.processInstanceDetails.overview.retentionPeriod') }}:</span>
                  <span>{{ $tc('trustx.processInstanceDetails.overview.retentionPeriodValue', instance.retentionPeriod) }}</span>
                </div>

                <div class="detail-row">
                  <span class="label">{{ $t('trustx.processInstanceDetails.overview.retentionExpiration') }}:</span>
                  <span>{{ instance.retentionExpiration }}</span>
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- VERIFICATION -->
    <v-card class="mb-4 pa-4">
      <v-card-title class="font-weight-bold">
        {{ $t('trustx.processInstanceDetails.verification.title') }}
      </v-card-title>
      <v-divider />
      <v-card
        v-for="(face, faceIndex) in faces"
        :key="face.id || faceIndex"
        class="mb-4 mt-4"
        outlined
      >
        <!-- HEADER -->
        <v-card-title class="d-flex justify-space-between align-start pa-4">
          {{ face.id }}
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- PORTRAIT FACE MATCH -->
          <v-card class="mb-4 pa-4" outlined>
            <!-- HEADER -->
            <v-card-title class="font-weight-bold pa-4">
              {{ $t('trustx.processInstanceDetails.verification.portraitFaceMatch') }}
            </v-card-title>

            <v-divider />

            <v-card-text class="pt-4">
              <v-row dense class="mx-0" align="stretch">
                <!-- SELFIE IMAGE -->
                <v-col cols="12" md="5" class="d-flex justify-center">
                  <div class="pa-3 d-flex align-center" style="width: 100%">
                    <div v-if="faceImageLoading" class="image-loading image-loading--portrait">
                      <v-progress-circular indeterminate color="primary" />
                    </div>
                    <v-img
                      v-else-if="faceImage"
                      :src="faceImage"
                      max-width="320"
                      height="320"
                      contain
                    />
                    <div v-else class="image-loading image-loading--portrait">
                      <v-icon color="grey">mdi-image-off</v-icon>
                    </div>
                  </div>
                </v-col>

                <!-- DOCUMENT PORTRAIT IMAGE -->
                <v-col cols="12" md="5" class="d-flex justify-center">
                  <div class="pa-3 d-flex align-center" style="width: 100%">
                    <div v-if="portraitImageLoading" class="image-loading image-loading--portrait">
                      <v-progress-circular indeterminate color="primary" />
                    </div>
                    <v-img
                      v-else-if="portraitImage"
                      :src="portraitImage"
                      max-width="320"
                      height="320"
                      contain
                    />
                    <div v-else class="image-loading image-loading--portrait">
                      <v-icon color="grey">mdi-image-off</v-icon>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <div
            v-for="(category, i) in face.report"
            :key="i"
            class="mb-4"
          >
            <v-card outlined>
              <v-card-title class="text-subtitle-2 font-weight-bold pa-4">
                {{ category.category }}
              </v-card-title>

              <v-divider />

              <v-list dense>
                <v-list-item
                  v-for="(item, index) in category.items"
                  :key="index"
                >
                  <v-list-item-icon>
                    <v-icon :color="outcomeColor(item.outcome)">
                      {{ outcomeIcon(item.outcome) }}
                    </v-icon>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title class="text-body-2">
                      {{ item.name }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </div>
        </v-card-text>
      </v-card>
    </v-card>

    <!-- DOCUMENTS -->
    <v-card
      v-for="(doc, docIndex) in documents"
      :key="doc.id || docIndex"
      class="mb-6 pa-4"
    >
      <!-- HEADER -->
      <v-card-title class="d-flex justify-space-between align-start">
        <!-- LEFT -->
        <div class="text-h6 font-weight-bold">
          {{ $t('trustx.processInstanceDetails.documents.title') }}
        </div>

        <!-- RIGHT -->
        <div class="d-flex flex-column align-end">
          <v-chip
            small
            color="orange"
            class="mt-1"
            dark
          >
            {{ doc.classification }}
          </v-chip>

          <div class="text-body-2 grey--text text--darken-1">
            Key: {{ doc.id }} |
            Type: {{ doc.type }}
          </div>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <v-row class="mx-0">
          <!-- ===================== -->
          <!-- LEFT COLUMN -->
          <!-- ===================== -->
          <v-col cols="12" lg="8">
            <!-- DOCUMENT IMAGES -->
            <v-card class="mt-4 pa-4" outlined>
              <v-card-title class="section-title">
                {{ $t('trustx.processInstanceDetails.documents.documentImages') }}
              </v-card-title>

              <v-divider />

              <v-card-text class="pt-4">
                <v-row dense class="mx-0">
                  <v-col
                    v-for="(img, i) in doc.documentImages"
                    :key="i"
                    cols="12"
                    sm="6"
                  >
                    <div outlined class="pa-3">
                      <div v-if="img.imageLoading" class="image-loading image-loading--document">
                        <v-progress-circular indeterminate color="primary" />
                      </div>
                      <v-img v-else-if="img.image" :src="img.image" aspect-ratio="1.6" contain />
                      <div v-else class="image-loading image-loading--document">
                        <v-icon color="grey">mdi-image-off</v-icon>
                      </div>

                      <v-card-actions class="justify-center flex-wrap pa-4">
                        <v-chip
                          v-for="(label, index) in img.labels"
                          :key="index"
                          small
                          class="ma-1"
                          :color="index === 0 ? 'blue' : 'amber darken-1 '"
                          :dark="index === 0"
                        >
                          {{ label }}
                        </v-chip>
                      </v-card-actions>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- SECURITY ELEMENTS -->
            <v-card class="mt-4 pa-4" outlined>
              <v-card-title class="section-title">
                {{ $t('trustx.processInstanceDetails.documents.securityElements') }}
              </v-card-title>

              <v-divider />

              <v-card-text class="pt-4">
                <v-row dense class="mx-0">
                  <v-col
                    v-for="(element, i) in doc.securityElements"
                    :key="i"
                    cols="6"
                    sm="4"
                    md="3"
                  >
                    <div outlined class="pa-3">
                      <div v-if="element.imageLoading" class="image-loading image-loading--security">
                        <v-progress-circular indeterminate color="primary" />
                      </div>
                      <v-img v-else-if="element.image" :src="element.image" aspect-ratio="1" contain />
                      <div v-else class="image-loading image-loading--security">
                        <v-icon color="grey">mdi-image-off</v-icon>
                      </div>

                      <v-card-actions class="justify-center">
                        <v-chip color="blue" small dark>
                          {{ element.label }}
                        </v-chip>
                      </v-card-actions>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- KEY DATA -->
            <v-card class="mt-4 pa-4" outlined>
              <v-card-title class="section-title">
                {{ $t('trustx.processInstanceDetails.documents.keyData') }}
              </v-card-title>

              <v-divider />

              <v-card-text class="pt-4">
                <div
                  v-for="(value, key, i) in doc.keyInfo"
                  :key="i"
                  class="d-flex justify-space-between align-center py-2 key-row"
                >
                  <span class="font-weight-medium">
                    {{ key }}
                  </span>

                  <span>
                    {{ value }}
                  </span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- ===================== -->
          <!-- RIGHT COLUMN -->
          <!-- ===================== -->
          <v-col cols="12" lg="4">
            <div
              v-for="(category, i) in doc.report"
              :key="i"
              class="mb-4"
            >
              <v-card outlined class="pa-4">
                <v-card-title class="text-subtitle-2 font-weight-bold pa-4">
                  {{ category.category }}
                </v-card-title>

                <v-divider />

                <v-list dense>
                  <v-list-item
                    v-for="(item, index) in category.items"
                    :key="index"
                  >
                    <v-list-item-icon>
                      <v-icon :color="outcomeColor(item.outcome)">
                        {{ outcomeIcon(item.outcome) }}
                      </v-icon>
                    </v-list-item-icon>

                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">
                        {{ item.name }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <!-- MORE DATA -->
          <v-card class="mt-4 pa-4" outlined>
            <v-card-title
              class="d-flex justify-space-between align-center"
              style="cursor: pointer;"
              @click="doc.showMoreData = !doc.showMoreData"
            >
              <span class="font-weight-bold">{{ $t('trustx.processInstanceDetails.documents.moreData') }}</span>
              <v-icon>
                {{ doc.showMoreData ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </v-card-title>

            <v-expand-transition>
              <div v-show="doc.showMoreData">
                <v-divider />

                <v-simple-table dense class="more-data-table">
                  <thead>
                    <tr>
                      <th>{{ $t('trustx.processInstanceDetails.documents.table.field') }}</th>
                      <th>{{ $t('trustx.processInstanceDetails.documents.table.mrz') }}</th>
                      <th>{{ $t('trustx.processInstanceDetails.documents.table.visual') }}</th>
                      <th>{{ $t('trustx.processInstanceDetails.documents.table.nfc') }}</th>
                      <th>{{ $t('trustx.processInstanceDetails.documents.table.barcode') }}</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="(field, i) in doc.biographicData"
                      :key="i"
                    >
                      <td class="font-weight-medium">
                        {{ field.label }}
                      </td>

                      <td class="wrap-cell">
                        {{ field.values?.mrzValue || '/' }}
                      </td>

                      <td class="wrap-cell">
                        {{ field.values?.visualZoneValue || '/' }}
                      </td>

                      <td class="wrap-cell">
                        {{ field.values?.nfcValue || '/' }}
                      </td>

                      <td class="wrap-cell">
                        {{ field.values?.barcodeValue || '/' }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>
            </v-expand-transition>
          </v-card>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- CHECKS -->
    <v-card class="pa-4">
      <v-card-title class="font-weight-bold">
        {{ $t('trustx.processInstanceDetails.checks.title') }}
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-4">
        <v-treeview
          :items="formattedChecks"
          activatable
          open-on-click
          dense
          hoverable
          transition
        >
          <template #prepend="{ item }">
            <v-icon v-if="item.icon" small>
              {{ item.icon }}
            </v-icon>
          </template>
        </v-treeview>
      </v-card-text>
    </v-card>

    <!-- TIMELINE -->
    <v-card class="mt-6 pa-4">
      <v-card-title class="font-weight-bold text-h6">
        {{ $t('trustx.processInstanceDetails.timeline.title') }}
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="timeline-scroll">
          <v-data-table
            :headers="timelineHeaders"
            :items="activities"
            dense
            hide-default-footer
            disable-pagination
            fixed-header
            height="400"
            class="elevation-0"
          >
            <!-- Activity -->
            <template #item.activityName="{ item }">
              <span class="py-2 d-block">
                {{ item.activityName }}
              </span>
            </template>

            <!-- Created -->
            <template #item.createdDate="{ item }">
              {{ formatDate(item.createdDate) }}
            </template>

            <!-- Status -->
            <template #item.status="{ item }">
              <div class="text-center">
                <v-icon
                  small
                  :color="activitySuccess(item) ? 'success' : 'grey'"
                >
                  {{ activitySuccess(item)
                    ? 'mdi-check-circle'
                    : 'mdi-progress-clock' }}
                </v-icon>
              </div>
            </template>

            <!-- Duration -->
            <template #item.elapsedMilliseconds="{ item }">
              <div class="text-right">
                {{ formatActivityDuration(item.elapsedMilliseconds) }}
              </div>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-text class="bpmn-card-content">
        <BpmnViewer v-if="bpmnXml" :xml="bpmnXml" :activities="activities" />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import trustx from '@/services/api/trustx.js'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../event-bus'
import FileSaver from 'file-saver'
import BpmnViewer from './BpmnViewer.vue'

export default {
  name: 'ProcessInstanceDetails',

  components: { BpmnViewer },

  data() {
    return {
      bpmnXml: null,
      faceImage: null,
      portraitImage: null,
      faceImageLoading: true,
      portraitImageLoading: true,
      processInstanceId: null,
      instance: {
        status: 'Accepted',
        referenceId: 'LMWKYEJHF08U2DUVJMW2FWIDY',
        name: null,
        email: null,
        phone: null,
        applicationDate: 'Feb 18, 2025 16:40',
        processVersion: 'na_face_match_no_answer v1',
        totalTime: '01:13 min',
        retentionPeriod: '30 days',
        retentionExpiration: 'Mar 21, 2025',
        faceImage: '/placeholder-face.jpg',
        faceMatchPerformed: false
      },
      checks: {},

      verificationChecks: [
        { label: 'Overall Quality', success: true },
        { label: 'Passive Face Liveness', success: true },
        { label: 'Face Template Generation', success: true },
        { label: 'Portrait Face Match', success: false }
      ],

      documents: [],
      faces: [],
      activities: [],
      documentExists: false,
      downloadDocumentLoading: false
    }
  },

  computed: {
    formattedChecks() {
      const buildTree = (obj) => {
        if (!obj || typeof obj !== 'object') return []

        return Object.keys(obj).map((key) => {
          const value = obj[key]

          if (typeof value !== 'object' || value === null) {
            return {
              id: key + Math.random(),
              name: `${key}: ${value}`,
              icon:
                    value === 'PASS' || value === 'APPROVE'
                      ? 'mdi-check-circle'
                      : value === 'FAIL' || value === 'DECLINE'
                        ? 'mdi-close-circle'
                        : null
            }
          }

          return {
            id: key + Math.random(),
            name: key,
            children: buildTree(value)
          }
        })
      }

      return buildTree(this.checks)
    },

    timelineHeaders() {
      return [
        { text: this.$t('trustx.processInstanceDetails.timeline.activity'), value: 'activityName' },
        { text: this.$t('trustx.processInstanceDetails.timeline.created'), value: 'createdDate' },
        { text: this.$t('trustx.processInstanceDetails.timeline.status'), value: 'status', sortable: false, align: 'center' },
        { text: this.$t('trustx.processInstanceDetails.timeline.timeSpent'), value: 'elapsedMilliseconds', align: 'end' }
      ]
    }
  },

  async mounted() {
    this.processInstanceId = this.$route.params.procId

    const [userData, checksResponse, processInstance, metrics, documentCheck] = await Promise.all([
      trustx.getUserData(this.processInstanceId),
      trustx.getUserDataChecks(this.processInstanceId),
      trustx.getProcessInstance(this.processInstanceId),
      trustx.getProcessInstanceMetrics(this.processInstanceId),
      trustx.documentExists(this.processInstanceId)
    ])

    this.documentExists = documentCheck.exists
    // const disposition = documentCheck.headers?.['content-disposition']
    this.activities = metrics.activities
    this.checks = checksResponse

    this.instance = {
      ...this.instance,
      referenceId: processInstance.id,
      status: processInstance.statusMapped,
      processVersion: `${processInstance.processDefnName} v${processInstance.processDefnVersion}`,
      applicationDate: this.formatDate(processInstance.createdAt),
      totalTime: this.formatDuration(processInstance.elapsedMilliseconds),
      retentionPeriod: processInstance.retentionDays,
      retentionExpiration: this.formatDate(processInstance.expiringDtm),
      name: this.buildFullName(processInstance.firstName, processInstance.lastName)
    }

    // console.log('this.instance', this.instance)

    this.documents = Object.entries(userData?.documents || {}).map(
      ([key, doc]) => ({
        id: doc.id,
        type: doc.type,
        classification: doc.classification,
        keyInfo: this.getKeyInfo(doc),
        report: this.getReports(doc),
        documentImages: this.buildDocumentImagePlaceholders(doc),
        securityElements: this.buildSecurityElementPlaceholders(doc),
        showMoreData: false,
        biographicData: doc.biographicData
      })
    )

    this.faces = Object.entries(userData?.faces || {}).map(
      ([key, face]) => ({
        id: face.id,
        report: this.getFaceReports(face),
        faceImage: null,
        imageHref: face.capture?.imageHref,
        imageLoading: Boolean(face.capture?.imageHref)
      })
    )

    this.bpmnXml = await trustx.getBpmnFile()

    this.loadOverviewImages(userData)
    this.loadFaceImages()
    this.loadDocumentImages()
  },

  methods: {
    getStatusColor(status) {
      if (status === 'Accepted') return 'success'
      if (status === 'Rejected') return 'error'
      return 'grey'
    },

    getLocalizedStatus(status) {
      const key = `trustx.processInstanceDetails.overview.statuses.${status}`
      const translated = this.$t(key)
      if (translated === key) {
        // console.warn(`[i18n] Missing translation for key: "${key}"`)
        return status
      }
      return translated
    },

    isSuccess(outcome) {
      return outcome === 'PASS' || outcome === 'APPROVE'
    },

    formatActivityDuration(ms) {
      if (ms == null) return '-'
      const t = (key) => this.$t(`trustx.duration.${key}`)
      if (ms < 1000) return t('lessThanOneSec')
      const seconds = Math.floor(ms / 1000)
      if (seconds < 60) return `${seconds} ${t('sec')}`
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      if (minutes < 60) return `${minutes}${t('min')} ${remainingSeconds}${t('sec')}`
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      return `${hours}${t('hour')} ${remainingMinutes}${t('min')}`
    },

    activitySuccess(activity) {
      return activity.elapsedMilliseconds != null
    },

    buildFullName(first, last) {
      return [first, last].filter(Boolean).join(' ')
    },

    mapOutcomeToStatus(outcome) {
      switch (outcome) {
        case 'APPROVE': return 'success'
        case 'REJECT': return 'error'
        case 'NA':
        default: return 'not-applicable'
      }
    },

    outcomeIcon(outcome) {
      switch (outcome) {
        case 'APPROVE': return 'mdi-check-circle'
        case 'DECLINE': return 'mdi-close-circle'
        case 'NA':
        default: return 'mdi-minus-circle-outline'
      }
    },

    outcomeColor(outcome) {
      switch (outcome) {
        case 'APPROVE': return 'green'
        case 'DECLINE': return 'red'
        case 'NA':
        default: return 'grey'
      }
    },

    getReports(doc) {
      const reportsByCategory = doc.reports?.reportsByCategory
      if (!reportsByCategory) return []

      const formatCategoryTitle = (key) =>
        key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()

      return Object.entries(reportsByCategory).map(([categoryKey, categoryValue]) => ({
        category: formatCategoryTitle(categoryKey),
        items: Object.values(categoryValue).map(item => ({
          id: item.id,
          name: item.name,
          description: item.description,
          outcome: item.outcome,
          status: this.mapOutcomeToStatus(item.outcome)
        }))
      }))
    },

    buildDocumentImagePlaceholders(doc) {
      const captures = doc?.captures
      if (!captures) return []

      const imageConfigs = [
        { side: this.$t('trustx.processInstanceDetails.document.side.front'), status: this.$t('trustx.processInstanceDetails.document.status.cropped'), href: captures?.CROPPED?.FRONT?.imageHref },
        { side: this.$t('trustx.processInstanceDetails.document.side.front'), status: this.$t('trustx.processInstanceDetails.document.status.uncropped'), href: captures?.UNCROPPED?.FRONT?.imageHref },
        { side: this.$t('trustx.processInstanceDetails.document.side.back'), status: this.$t('trustx.processInstanceDetails.document.status.cropped'), href: captures?.CROPPED?.BACK?.imageHref },
        { side: this.$t('trustx.processInstanceDetails.document.side.back'), status: this.$t('trustx.processInstanceDetails.document.status.uncropped'), href: captures?.UNCROPPED?.BACK?.imageHref }
      ].filter(config => config.href)

      // console.log('captures', captures)
      // console.log('imageConfigs', imageConfigs)

      return imageConfigs.map(config => ({
        side: config.side,
        status: config.status,
        labels: [config.side, config.status],
        imageHref: config.href,
        image: null,
        imageLoading: true
      }))
    },

    buildSecurityElementPlaceholders(doc) {
      const segmentedImages = doc?.segmentedImages
      if (!segmentedImages) return []

      const formatLabel = (id) =>
        id.toLowerCase().split('_').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')

      return Object.values(segmentedImages)
        .filter(segment => segment?.capture?.imageHref)
        .map(segment => ({
          label: formatLabel(segment.id),
          imageHref: segment.capture.imageHref,
          image: null,
          imageLoading: true
        }))
    },

    async fetchImageByHref(imageHref) {
      if (!imageHref) return null
      try {
        const response = await trustx.getProcessInstanceData(this.processInstanceId, imageHref)
        if (!response?.data) return null
        return `data:${response.contentType};base64,${response.data}`
      } catch {
        return null
      }
    },

    async loadOverviewImages(userData) {
      const face1 = userData?.faces?.face1
      if (face1?.capture?.imageHref) {
        try {
          this.faceImage = await this.fetchImageByHref(face1.capture.imageHref)
        } finally {
          this.faceImageLoading = false
        }
      } else {
        this.faceImageLoading = false
      }

      const portraitImageHref = userData?.documents?.doc1?.portraitImage?.capture?.imageHref
      if (portraitImageHref) {
        try {
          this.portraitImage = await this.fetchImageByHref(portraitImageHref)
        } finally {
          this.portraitImageLoading = false
        }
      } else {
        this.portraitImageLoading = false
      }
    },

    async loadFaceImages() {
      await Promise.all(
        this.faces.map(async (face) => {
          if (!face.imageHref) {
            face.imageLoading = false
            return
          }
          try {
            face.faceImage = await this.fetchImageByHref(face.imageHref)
          } finally {
            face.imageLoading = false
          }
        })
      )
    },

    async loadDocumentImages() {
      await Promise.all(
        this.documents.map(async (doc) => {
          await Promise.all(
            doc.documentImages.map(async (img) => {
              try {
                img.image = await this.fetchImageByHref(img.imageHref)
              } finally {
                img.imageLoading = false
              }
            })
          )
          await Promise.all(
            doc.securityElements.map(async (element) => {
              try {
                element.image = await this.fetchImageByHref(element.imageHref)
              } finally {
                element.imageLoading = false
              }
            })
          )
        })
      )
    },

    getKeyInfo(doc) {
      const keyBiographicData = doc.keyBiographicData
      if (!keyBiographicData) return []
      return Object.values(keyBiographicData).reduce((acc, field) => {
        const key = field.label || field.fieldName
        const value = field.value ?? ''
        if (key) acc[key] = value
        return acc
      }, {})
    },

    getFaceReports(face) {
      const reportsByCategory = face.reports?.reportsByCategory
      if (!reportsByCategory) return []

      const formatCategoryTitle = (key) =>
        key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()

      return Object.entries(reportsByCategory).map(([categoryKey, categoryValue]) => ({
        category: formatCategoryTitle(categoryKey),
        items: Object.values(categoryValue).map(item => ({
          id: item.id,
          name: item.name,
          description: item.description,
          outcome: item.outcome,
          status: this.mapOutcomeToStatus(item.outcome)
        }))
      }))
    },

    async downloadDocument() {
      this.downloadDocumentLoading = true
      try {
        const documentData = await trustx.downloadDocument(this.processInstanceId)
        const downloadFileName = `${this.processInstanceId}.pdf`

        await FileSaver.saveAs(documentData, downloadFileName)
      } catch (error) {
        showToastError(this.getErrorMessage(error))
      } finally {
        this.downloadDocumentLoading = false
      }
    }
  }
}
</script>

<style scoped>
.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.label {
  font-weight: 500;
  color: #6b7280;
}

.section-title {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 12px;
}

.key-row {
  border-bottom: 1px solid #eee;
  padding-bottom: 6px;
}

.more-data-table table {
  table-layout: fixed;
  width: 100%;
}

.more-data-table th,
.more-data-table td {
  width: 20%;
  word-break: break-word;
  white-space: normal;
  vertical-align: top;
}

.timeline-scroll {
  border-radius: 4px;
}

.image-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.image-loading--avatar {
  width: 100%;
  height: 100%;
}

.image-loading--portrait {
  width: 320px;
  height: 200px;
}

.image-loading--document {
  width: 100%;
  aspect-ratio: 1.6;
  min-height: 140px;
}

.image-loading--security {
  width: 100%;
  aspect-ratio: 1;
  min-height: 110px;
}

.bpmn-card-content {
  position: relative;
  height: 70vh;
  padding: 0;
}
</style>
